import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card, CardBody, Button, Select, SelectItem } from '@nextui-org/react';
import { read, utils } from 'xlsx';
import { movementsExcelMapping, requiredFields, MovementsRow } from '../../utils/movements-excel-mapping';
import { toast } from 'react-toastify';
import { generateMovementsTemplate } from '../../utils/movements-template';
import { supabase } from 'common/src/supabase';

export const MovementsUpload = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [selectedExporter, setSelectedExporter] = useState<string>('');
  const [fileData, setFileData] = useState<MovementsRow[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [exporters, setExporters] = useState<{ id: string; name: string; }[]>([]);
  const [loadingExporters, setLoadingExporters] = useState(true);

  useEffect(() => {
    const fetchExporters = async () => {
      try {
        const { data, error } = await supabase
          .from('exporters')
          .select('id, name')
          .order('name');

        if (error) throw error;
        setExporters(data || []);
      } catch (error) {
        toast.error('Error al cargar exportadores');
      } finally {
        setLoadingExporters(false);
      }
    };

    fetchExporters();
  }, []);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = utils.sheet_to_json(worksheet);

        // Validate and transform data
        const transformedData = jsonData.map((row: any, index: number) => {
          const transformedRow: Partial<MovementsRow> = {};

          // Map Spanish column names to internal names
          Object.entries(movementsExcelMapping).forEach(([spanishKey, internalKey]) => {
            if (row[spanishKey] !== undefined) {
              transformedRow[internalKey] = row[spanishKey];
            }
          });

          // Validate required fields
          requiredFields.forEach(field => {
            if (!transformedRow[field]) {
              throw new Error(`Fila ${index + 2}: Campo requerido "${field}" está vacío`);
            }
          });

          return transformedRow as MovementsRow;
        });

        setFileData(transformedData);
      } catch (error) {
        toast.error(error.message);
        setFileData([]);
      }
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1
  });

  const handleUpload = async () => {
    if (!selectedExporter) {
      toast.error('Por favor seleccione un exportador');
      return;
    }

    if (fileData.length === 0) {
      toast.error('Por favor cargue un archivo válido');
      return;
    }

    setIsUploading(true);
    try {
      // First, delete existing records with the same criteria
      const { error: deleteError } = await supabase
        .from('movements')
        .delete()
        .eq('exporter_id', selectedExporter)
        .eq('season', fileData[0].season) // Assuming all records are from the same season
        .eq('species_id', fileData[0].species_id); // Assuming all records are from the same species

      if (deleteError) throw deleteError;

      // Then insert new records
      const { error: insertError } = await supabase
        .from('movements')
        .insert(
          fileData.map(row => ({
            ...row,
            exporter_id: selectedExporter,
            created_at: new Date().toISOString()
          }))
        );

      if (insertError) throw insertError;
      
      toast.success('Archivo cargado exitosamente');
      setFileData([]);
      setFileName('');
      setSelectedExporter('');
    } catch (error) {
      toast.error('Error al cargar el archivo: ' + error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const downloadTemplate = () => {
    generateMovementsTemplate();
  };

  return (
    <Card className="w-full">
      <CardBody className="gap-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold">Cargar Anticipos</h2>
          <Button
            variant="flat"
            onPress={downloadTemplate}
          >
            Descargar Plantilla Tipo
          </Button>
        </div>

        <Select
          label="Exportador"
          placeholder="Seleccione un exportador"
          value={selectedExporter}
          onChange={(e) => setSelectedExporter(e.target.value)}
          isDisabled={loadingExporters}
        >
          {exporters.map((exporter) => (
            <SelectItem key={exporter.id} value={exporter.id}>
              {exporter.name}
            </SelectItem>
          ))}
        </Select>

        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
            ${isDragActive ? 'border-primary bg-primary/10' : 'border-divider'}`}
        >
          <input {...getInputProps()} />
          {fileName ? (
            <p className="text-success">Archivo seleccionado: {fileName}</p>
          ) : (
            <p>Arrastre un archivo Excel o haga click para seleccionar</p>
          )}
        </div>

        {fileData.length > 0 && (
          <div className="text-sm text-default-500">
            {fileData.length} filas encontradas
          </div>
        )}

        <Button
          color="primary"
          isLoading={isUploading}
          onPress={handleUpload}
          isDisabled={!selectedExporter || fileData.length === 0}
          className="w-full"
        >
          Cargar Archivo
        </Button>
      </CardBody>
    </Card>
  );
};